import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Users,
    Calendar,
    Clock,
    DollarSign,
    Plus,
    Pencil,
    Trash2,
    Loader2,
    LogIn,
    LogOut,
    CheckCircle2,
    XCircle,
    Search,
    Copy,
    FileText,
} from 'lucide-react';
import { employeeApi } from '@/lib/api';
import { EmployeeSchedule, EmployeeAttendance, EmployeePayroll, ScheduleTemplate, ScheduleTemplateDay } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { format, differenceInMinutes } from 'date-fns';
import { es } from 'date-fns/locale';

const DAYS_OF_WEEK = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAYS_SHORT = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

interface Employee {
    id: string;
    full_name: string;
    email: string;
    role: string;
}

// Default template days structure
const getDefaultDays = (): ScheduleTemplateDay[] => [
    { day: 0, start: '09:00', end: '17:00', active: false },
    { day: 1, start: '09:00', end: '17:00', active: true },
    { day: 2, start: '09:00', end: '17:00', active: true },
    { day: 3, start: '09:00', end: '17:00', active: true },
    { day: 4, start: '09:00', end: '17:00', active: true },
    { day: 5, start: '09:00', end: '17:00', active: true },
    { day: 6, start: '09:00', end: '17:00', active: false },
];

export default function EmployeesPage() {
    const { gym, user, hasRole } = useAuth();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState<'employees' | 'schedules' | 'attendance' | 'payroll'>('employees');
    const [isLoading, setIsLoading] = useState(true);

    // Data states
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [schedules, setSchedules] = useState<EmployeeSchedule[]>([]);
    const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
    const [attendance, setAttendance] = useState<EmployeeAttendance[]>([]);
    const [payroll, setPayroll] = useState<EmployeePayroll[]>([]);
    const [myActiveAttendance, setMyActiveAttendance] = useState<EmployeeAttendance | null>(null);

    // Template Dialog
    const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<ScheduleTemplate | null>(null);
    const [templateName, setTemplateName] = useState('');
    const [templateDays, setTemplateDays] = useState<ScheduleTemplateDay[]>(getDefaultDays());

    // Assign Template Dialog
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    // Payroll Dialog
    const [isPayrollDialogOpen, setIsPayrollDialogOpen] = useState(false);
    const [editingPayroll, setEditingPayroll] = useState<EmployeePayroll | null>(null);

    // Form states
    const [payrollForm, setPayrollForm] = useState({
        user_id: '',
        period_start: '',
        period_end: '',
        base_salary: 0,
        bonuses: 0,
        deductions: 0,
        notes: '',
    });

    const [search, setSearch] = useState('');

    const isAdmin = hasRole(['gym_admin']);
    const isEmployee = hasRole(['gym_staff', 'gym_supervisor']);

    useEffect(() => {
        if (gym?.gym_id) {
            loadData();
        }
    }, [gym?.gym_id, activeTab]);

    async function loadData() {
        setIsLoading(true);
        try {
            if (activeTab === 'employees' || activeTab === 'schedules' || activeTab === 'payroll') {
                const empRes = await employeeApi.listEmployees(gym!.gym_id);
                if (empRes.success) setEmployees(empRes.employees);
            }

            if (activeTab === 'schedules') {
                const schRes = await employeeApi.listSchedules(gym!.gym_id);
                if (schRes.success) setSchedules(schRes.schedules);

                const tplRes = await employeeApi.listTemplates(gym!.gym_id);
                if (tplRes.success) setTemplates(tplRes.templates);
            }

            if (activeTab === 'attendance') {
                const attRes = await employeeApi.listAttendance(gym!.gym_id);
                if (attRes.success) setAttendance(attRes.attendance);

                // Check my active attendance
                if (user?.user_id) {
                    const activeRes = await employeeApi.getActiveAttendance(gym!.gym_id, user.user_id);
                    if (activeRes.success) setMyActiveAttendance(activeRes.attendance);
                }
            }

            if (activeTab === 'payroll') {
                const payRes = await employeeApi.listPayroll(gym!.gym_id);
                if (payRes.success) setPayroll(payRes.payroll);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            Sentry.captureException(error, { tags: { operation: 'employees_load_data' } });
            toast({ title: 'Error', description: 'No se pudo cargar la información', variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    }

    // Template handlers
    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            toast({ title: 'Error', description: 'El nombre es requerido', variant: 'destructive' });
            return;
        }

        try {
            if (editingTemplate) {
                await employeeApi.updateTemplate(editingTemplate.id, {
                    name: templateName,
                    days: templateDays,
                });
                toast({ title: 'Plantilla actualizada' });
            } else {
                await employeeApi.createTemplate({
                    gym_id: gym!.gym_id,
                    name: templateName,
                    days: templateDays,
                });
                toast({ title: 'Plantilla creada', description: 'Ahora puedes asignarla a tus empleados' });
            }
            setIsTemplateDialogOpen(false);
            resetTemplateForm();
            loadData();
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_save_template' } });
            toast({ title: 'Error', description: 'No se pudo guardar la plantilla', variant: 'destructive' });
        }
    };

    const handleDeleteTemplate = async (id: string) => {
        if (!confirm('¿Eliminar esta plantilla?')) return;
        try {
            await employeeApi.deleteTemplate(id);
            toast({ title: 'Plantilla eliminada' });
            loadData();
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_delete_template' } });
            toast({ title: 'Error', variant: 'destructive' });
        }
    };

    const handleAssignTemplate = async () => {
        if (!selectedEmployeeId || !selectedTemplateId) {
            toast({ title: 'Error', description: 'Selecciona empleado y plantilla', variant: 'destructive' });
            return;
        }

        try {
            await employeeApi.applyTemplateToEmployee(gym!.gym_id, selectedEmployeeId, selectedTemplateId);
            toast({ title: 'Horario asignado', description: 'El empleado tiene el nuevo horario' });
            setIsAssignDialogOpen(false);
            setSelectedEmployeeId('');
            setSelectedTemplateId('');
            loadData();
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_assign_template' } });
            toast({ title: 'Error', description: 'No se pudo asignar el horario', variant: 'destructive' });
        }
    };

    const resetTemplateForm = () => {
        setTemplateName('');
        setTemplateDays(getDefaultDays());
        setEditingTemplate(null);
    };

    const openEditTemplate = (template: ScheduleTemplate) => {
        setEditingTemplate(template);
        setTemplateName(template.name);
        // Ensure we have all 7 days with complete data
        const days = getDefaultDays().map(defaultDay => {
            const existing = template.days?.find(d => d.day === defaultDay.day);
            return existing || defaultDay;
        });
        setTemplateDays(days);
        setIsTemplateDialogOpen(true);
    };

    const updateTemplateDay = (dayIndex: number, field: keyof ScheduleTemplateDay, value: any) => {
        setTemplateDays(prev => prev.map((d, i) =>
            i === dayIndex ? { ...d, [field]: value } : d
        ));
    };

    // Attendance handlers
    const handleClockIn = async () => {
        if (!user?.user_id || !gym?.gym_id) return;
        try {
            const res = await employeeApi.clockIn(gym.gym_id, user.user_id);
            if (res.success) {
                toast({ title: '¡Entrada registrada!', description: `Bienvenido, ${user.name}` });
                loadData();
            } else {
                toast({ title: 'Error', description: res.error, variant: 'destructive' });
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_clock_in' } });
            toast({ title: 'Error', description: 'No se pudo registrar la entrada', variant: 'destructive' });
        }
    };

    const handleClockOut = async () => {
        if (!myActiveAttendance) return;
        try {
            const res = await employeeApi.clockOut(myActiveAttendance.id);
            if (res.success) {
                toast({ title: '¡Salida registrada!', description: 'Hasta pronto' });
                loadData();
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_clock_out' } });
            toast({ title: 'Error', description: 'No se pudo registrar la salida', variant: 'destructive' });
        }
    };

    // Payroll handlers
    const handleCreatePayroll = async () => {
        try {
            const res = await employeeApi.createPayroll({
                gym_id: gym!.gym_id,
                user_id: payrollForm.user_id,
                period_start: payrollForm.period_start,
                period_end: payrollForm.period_end,
                base_salary: payrollForm.base_salary,
                bonuses: payrollForm.bonuses,
                deductions: payrollForm.deductions,
                notes: payrollForm.notes,
            });
            if (res.success) {
                toast({ title: 'Nómina creada' });
                setIsPayrollDialogOpen(false);
                resetPayrollForm();
                loadData();
            }
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_create_payroll' } });
            toast({ title: 'Error', description: 'No se pudo crear la nómina', variant: 'destructive' });
        }
    };

    const handleMarkAsPaid = async (id: string) => {
        try {
            const res = await employeeApi.markPayrollAsPaid(id, gym!.gym_id);

            // Build descriptive message
            const parts: string[] = [];
            if (res.expenseCreated) parts.push('gasto registrado en balance general');
            if (res.cashMovementCreated) parts.push('egreso registrado en caja');
            else parts.push('sin caja abierta');

            toast({
                title: '✅ Pago registrado',
                description: parts.join(' • ').charAt(0).toUpperCase() + parts.join(' • ').slice(1)
            });
            loadData();
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_mark_paid' } });
            toast({ title: 'Error', variant: 'destructive' });
        }
    };

    const handleDeletePayroll = async (id: string) => {
        try {
            await employeeApi.deletePayroll(id);
            toast({ title: 'Nómina eliminada' });
            loadData();
        } catch (error) {
            Sentry.captureException(error, { tags: { operation: 'employees_delete_payroll' } });
            toast({ title: 'Error', variant: 'destructive' });
        }
    };

    const resetPayrollForm = () => {
        setPayrollForm({
            user_id: '',
            period_start: '',
            period_end: '',
            base_salary: 0,
            bonuses: 0,
            deductions: 0,
            notes: '',
        });
    };

    // Group schedules by employee
    const schedulesByEmployee = employees.map(emp => {
        const empSchedules = schedules.filter(s => s.user_id === emp.id);
        return {
            employee: emp,
            schedules: empSchedules,
            // Create a lookup for quick access
            dayMap: empSchedules.reduce((acc, s) => {
                acc[s.day_of_week] = s;
                return acc;
            }, {} as Record<number, EmployeeSchedule>)
        };
    }).filter(e => e.schedules.length > 0 || isAdmin);

    const filteredEmployees = employees.filter(e =>
        e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        e.email?.toLowerCase().includes(search.toLowerCase())
    );

    if (isLoading) {
        return (
            <DashboardLayout title="Empleados">
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Gestión de Empleados" subtitle="Horarios, asistencia y nómina">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
                <Button
                    variant={activeTab === 'employees' ? 'gradient' : 'outline'}
                    onClick={() => setActiveTab('employees')}
                    className="flex-1 sm:flex-none"
                >
                    <Users className="mr-2 h-4 w-4" />
                    Empleados
                </Button>
                <Button
                    variant={activeTab === 'schedules' ? 'gradient' : 'outline'}
                    onClick={() => setActiveTab('schedules')}
                    className="flex-1 sm:flex-none"
                >
                    <Calendar className="mr-2 h-4 w-4" />
                    Horarios
                </Button>
                <Button
                    variant={activeTab === 'attendance' ? 'gradient' : 'outline'}
                    onClick={() => setActiveTab('attendance')}
                    className="flex-1 sm:flex-none"
                >
                    <Clock className="mr-2 h-4 w-4" />
                    Asistencia
                </Button>
                {isAdmin && (
                    <Button
                        variant={activeTab === 'payroll' ? 'gradient' : 'outline'}
                        onClick={() => setActiveTab('payroll')}
                        className="flex-1 sm:flex-none"
                    >
                        <DollarSign className="mr-2 h-4 w-4" />
                        Nómina
                    </Button>
                )}
            </div>

            {/* Employees Tab */}
            {activeTab === 'employees' && (
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Empleados ({employees.length})
                            </CardTitle>
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar empleado..."
                                    className="pl-9"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nombre</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Rol</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredEmployees.map((emp) => (
                                        <TableRow key={emp.id}>
                                            <TableCell className="font-medium">{emp.full_name || 'Sin nombre'}</TableCell>
                                            <TableCell>{emp.email}</TableCell>
                                            <TableCell>
                                                <Badge variant={emp.role === 'gym_supervisor' ? 'default' : 'secondary'}>
                                                    {emp.role === 'gym_supervisor' ? 'Supervisor' : 'Staff'}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredEmployees.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                                No hay empleados registrados. Ve a Usuarios para crear uno con rol Staff o Supervisor.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Schedules Tab - NEW IMPROVED UI */}
            {activeTab === 'schedules' && (
                <div className="space-y-6">
                    {/* Templates Section */}
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="h-5 w-5" />
                                        Plantillas de Horario
                                    </CardTitle>
                                    <CardDescription className="hidden sm:block">
                                        Crea turnos predefinidos y asígnalos a múltiples empleados
                                    </CardDescription>
                                </div>
                                {isAdmin && (
                                    <Button variant="gradient" size="sm" className="w-full sm:w-auto" onClick={() => { resetTemplateForm(); setIsTemplateDialogOpen(true); }}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Nueva Plantilla
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {templates.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                    <p>No hay plantillas creadas.</p>
                                    <p className="text-sm">Crea tu primera plantilla como "Turno Mañana" o "Turno Noche"</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {templates.map(template => {
                                        const activeDays = template.days?.filter(d => d.active) || [];
                                        return (
                                            <div
                                                key={template.id}
                                                className="border rounded-xl p-4 bg-card hover:border-primary/50 transition-colors"
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div>
                                                        <h3 className="font-semibold text-lg">{template.name}</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            {activeDays.length} días activos
                                                        </p>
                                                    </div>
                                                    {isAdmin && (
                                                        <div className="flex gap-1">
                                                            <Button size="icon" variant="ghost" onClick={() => openEditTemplate(template)}>
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteTemplate(template.id)}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Day Pills */}
                                                <div className="flex gap-1 mb-3">
                                                    {DAYS_SHORT.map((dayLabel, idx) => {
                                                        const dayData = template.days?.find(d => d.day === idx);
                                                        const isActive = dayData?.active;
                                                        return (
                                                            <div
                                                                key={idx}
                                                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isActive
                                                                    ? 'bg-primary text-primary-foreground'
                                                                    : 'bg-muted text-muted-foreground'
                                                                    }`}
                                                            >
                                                                {dayLabel}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Time display */}
                                                {activeDays.length > 0 && (
                                                    <div className="text-sm text-muted-foreground">
                                                        <Clock className="h-3 w-3 inline mr-1" />
                                                        {activeDays[0].start} - {activeDays[0].end}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Assign Template + Employee Schedule Matrix */}
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Calendar className="h-5 w-5" />
                                        Horarios por Empleado
                                    </CardTitle>
                                    <CardDescription className="hidden sm:block">
                                        Vista semanal de todos los empleados
                                    </CardDescription>
                                </div>
                                {isAdmin && templates.length > 0 && (
                                    <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setIsAssignDialogOpen(true)}>
                                        <Copy className="mr-2 h-4 w-4" />
                                        Asignar Plantilla
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="min-w-[150px]">Empleado</TableHead>
                                            {DAYS_OF_WEEK.map((day, idx) => (
                                                <TableHead key={idx} className="text-center min-w-[100px]">
                                                    {day.slice(0, 3)}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {schedulesByEmployee.map(({ employee, dayMap }) => (
                                            <TableRow key={employee.id}>
                                                <TableCell className="font-medium">
                                                    {employee.full_name || employee.email}
                                                </TableCell>
                                                {DAYS_OF_WEEK.map((_, idx) => {
                                                    const schedule = dayMap[idx];
                                                    return (
                                                        <TableCell key={idx} className="text-center">
                                                            {schedule ? (
                                                                <Badge variant="outline" className="text-xs whitespace-nowrap">
                                                                    {schedule.start_time.slice(0, 5)}-{schedule.end_time.slice(0, 5)}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted-foreground text-xs">—</span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                        {schedulesByEmployee.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                                    No hay horarios asignados. Crea una plantilla y asígnala a un empleado.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
                <div className="space-y-6">
                    {/* Clock In/Out Card for employees */}
                    {(isEmployee || isAdmin) && (
                        <Card className="border-primary/20 bg-primary/5">
                            <CardHeader>
                                <CardTitle className="text-lg">Mi Asistencia</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row items-center gap-4">
                                    {myActiveAttendance ? (
                                        <>
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground">Entrada registrada a las:</p>
                                                <p className="text-2xl font-bold text-primary">
                                                    {format(new Date(myActiveAttendance.check_in), 'HH:mm', { locale: es })}
                                                </p>
                                            </div>
                                            <Button variant="destructive" size="lg" onClick={handleClockOut}>
                                                <LogOut className="mr-2 h-5 w-5" />
                                                Registrar Salida
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground">No tienes una entrada activa</p>
                                                <p className="text-2xl font-bold">{format(new Date(), 'HH:mm', { locale: es })}</p>
                                            </div>
                                            <Button variant="gradient" size="lg" onClick={handleClockIn}>
                                                <LogIn className="mr-2 h-5 w-5" />
                                                Registrar Entrada
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Attendance History */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="h-5 w-5" />
                                Historial de Asistencia
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Empleado</TableHead>
                                            <TableHead>Fecha</TableHead>
                                            <TableHead>Entrada</TableHead>
                                            <TableHead>Salida</TableHead>
                                            <TableHead>Duración</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {attendance.slice(0, 20).map((att) => {
                                            const duration = att.check_out
                                                ? differenceInMinutes(new Date(att.check_out), new Date(att.check_in))
                                                : null;
                                            const hours = duration ? Math.floor(duration / 60) : 0;
                                            const mins = duration ? duration % 60 : 0;

                                            return (
                                                <TableRow key={att.id}>
                                                    <TableCell className="font-medium">{att.user_name || 'Empleado'}</TableCell>
                                                    <TableCell>{format(new Date(att.check_in), 'd MMM yyyy', { locale: es })}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                                                            {format(new Date(att.check_in), 'HH:mm')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {att.check_out ? (
                                                            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
                                                                {format(new Date(att.check_out), 'HH:mm')}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="secondary">Activo</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {duration !== null ? `${hours}h ${mins}m` : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {attendance.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                    No hay registros de asistencia.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Payroll Tab */}
            {activeTab === 'payroll' && isAdmin && (
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <CardTitle className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5" />
                                Nómina
                            </CardTitle>
                            <Button variant="gradient" size="sm" className="w-full sm:w-auto" onClick={() => { resetPayrollForm(); setEditingPayroll(null); setIsPayrollDialogOpen(true); }}>
                                <Plus className="mr-2 h-4 w-4" />
                                Crear Nómina
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Empleado</TableHead>
                                        <TableHead>Periodo</TableHead>
                                        <TableHead className="text-right">Salario Base</TableHead>
                                        <TableHead className="text-right">Bonos</TableHead>
                                        <TableHead className="text-right">Deducciones</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payroll.map((pay) => (
                                        <TableRow key={pay.id}>
                                            <TableCell className="font-medium">{pay.user_name || 'Empleado'}</TableCell>
                                            <TableCell>
                                                {format(new Date(pay.period_start), 'd MMM', { locale: es })} - {format(new Date(pay.period_end), 'd MMM', { locale: es })}
                                            </TableCell>
                                            <TableCell className="text-right">${pay.base_salary.toFixed(2)}</TableCell>
                                            <TableCell className="text-right text-green-600">+${pay.bonuses.toFixed(2)}</TableCell>
                                            <TableCell className="text-right text-red-600">-${pay.deductions.toFixed(2)}</TableCell>
                                            <TableCell className="text-right font-bold">${pay.total_paid.toFixed(2)}</TableCell>
                                            <TableCell>
                                                {pay.status === 'paid' ? (
                                                    <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                                                        <CheckCircle2 className="mr-1 h-3 w-3" />
                                                        Pagado
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">
                                                        <XCircle className="mr-1 h-3 w-3" />
                                                        Pendiente
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right space-x-2">
                                                {pay.status === 'pending' && (
                                                    <Button size="sm" variant="outline" onClick={() => handleMarkAsPaid(pay.id)}>
                                                        Marcar Pagado
                                                    </Button>
                                                )}
                                                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeletePayroll(pay.id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {payroll.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                                No hay nóminas registradas.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Template Dialog - Weekly Grid Editor */}
            <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla de Horario'}</DialogTitle>
                        <DialogDescription>
                            Define los días y horarios.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre de la Plantilla</Label>
                            <Input
                                placeholder="Ej: Turno Mañana, Turno Nocturno..."
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                            />
                        </div>

                        {/* Weekly Grid - Mobile Optimized */}
                        <div className="space-y-2">
                            <Label>Días de Trabajo</Label>
                            <div className="border rounded-lg divide-y">
                                {templateDays.map((dayData, idx) => (
                                    <div key={idx} className="p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-medium flex items-center gap-2">
                                                <Switch
                                                    checked={dayData.active}
                                                    onCheckedChange={(checked) => updateTemplateDay(idx, 'active', checked)}
                                                />
                                                <span className={dayData.active ? '' : 'text-muted-foreground'}>
                                                    {DAYS_OF_WEEK[idx]}
                                                </span>
                                            </div>
                                            {!dayData.active && (
                                                <span className="text-xs text-muted-foreground">Libre</span>
                                            )}
                                        </div>
                                        {dayData.active && (
                                            <div className="flex items-center gap-2 ml-10">
                                                <Input
                                                    type="time"
                                                    value={dayData.start}
                                                    onChange={(e) => updateTemplateDay(idx, 'start', e.target.value)}
                                                    className="flex-1"
                                                />
                                                <span className="text-muted-foreground text-sm">a</span>
                                                <Input
                                                    type="time"
                                                    value={dayData.end}
                                                    onChange={(e) => updateTemplateDay(idx, 'end', e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleSaveTemplate}>
                            {editingTemplate ? 'Actualizar' : 'Crear Plantilla'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Template Dialog */}
            <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar Plantilla a Empleado</DialogTitle>
                        <DialogDescription>
                            Selecciona un empleado y la plantilla de horario que deseas asignarle.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Empleado</Label>
                            <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar empleado" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name || emp.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Plantilla de Horario</Label>
                            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar plantilla" />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((tpl) => (
                                        <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedTemplateId && (
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="text-sm text-muted-foreground mb-2">Vista previa:</p>
                                <div className="flex gap-1">
                                    {DAYS_SHORT.map((dayLabel, idx) => {
                                        const template = templates.find(t => t.id === selectedTemplateId);
                                        const dayData = template?.days?.find(d => d.day === idx);
                                        const isActive = dayData?.active;
                                        return (
                                            <div
                                                key={idx}
                                                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isActive
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-muted text-muted-foreground'
                                                    }`}
                                            >
                                                {dayLabel}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleAssignTemplate}>Asignar Horario</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Payroll Dialog */}
            <Dialog open={isPayrollDialogOpen} onOpenChange={setIsPayrollDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Crear Nómina</DialogTitle>
                        <DialogDescription>Registra el pago de un empleado.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Empleado</Label>
                            <Select value={payrollForm.user_id} onValueChange={(v) => setPayrollForm({ ...payrollForm, user_id: v })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar empleado" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name || emp.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Inicio Periodo</Label>
                                <Input type="date" value={payrollForm.period_start} onChange={(e) => setPayrollForm({ ...payrollForm, period_start: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Fin Periodo</Label>
                                <Input type="date" value={payrollForm.period_end} onChange={(e) => setPayrollForm({ ...payrollForm, period_end: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Salario Base ($)</Label>
                            <Input type="number" value={payrollForm.base_salary} onChange={(e) => setPayrollForm({ ...payrollForm, base_salary: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Bonos ($)</Label>
                                <Input type="number" value={payrollForm.bonuses} onChange={(e) => setPayrollForm({ ...payrollForm, bonuses: parseFloat(e.target.value) || 0 })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Deducciones ($)</Label>
                                <Input type="number" value={payrollForm.deductions} onChange={(e) => setPayrollForm({ ...payrollForm, deductions: parseFloat(e.target.value) || 0 })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Notas (opcional)</Label>
                            <Input value={payrollForm.notes} onChange={(e) => setPayrollForm({ ...payrollForm, notes: e.target.value })} placeholder="Notas adicionales..." />
                        </div>
                        <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                            <div className="flex justify-between items-center">
                                <span className="font-medium">Total a Pagar:</span>
                                <span className="text-2xl font-bold text-primary">
                                    ${(payrollForm.base_salary + payrollForm.bonuses - payrollForm.deductions).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsPayrollDialogOpen(false)}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleCreatePayroll}>Crear Nómina</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
